/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { PropsWithChildren, useLayoutEffect, useRef } from 'react';
import * as THREE from 'three';

type MeshLike = THREE.Mesh | THREE.SkinnedMesh | THREE.InstancedMesh;

const ORIG_KEY = '__overrideOrigMaterial';
const CLONE_KEY = '__overrideClonedMaterial';

export type OverrideMaterialProps = PropsWithChildren<{
  /** The material used to override. If null/undefined, originals are restored. */
  material?: THREE.Material | null;
  /** Enable/disable overriding without unmounting the component. */
  active?: boolean;
  /** Reuse a single shared material instance for all meshes (faster, default). */
  shareMaterial?: boolean;
  /** If true and shareMaterial=false, dispose cloned materials on cleanup. */
  disposeClones?: boolean;
  /** Raise draw order to render on top (e.g., ghost effects). Leave undefined to keep as-is. */
  renderOrder?: number;

  /** Only override meshes that pass this predicate. If omitted, all meshes are eligible. */
  include?: (obj: THREE.Object3D) => boolean;
  /** Skip meshes that pass this predicate. Runs after `include`. */
  exclude?: (obj: THREE.Object3D) => boolean;
}>;

/**
 * Overrides the material of all meshes in its subtree, then restores on cleanup.
 * Example:
 *   <OverrideMaterial material={ghostMat}><VirtualCourse .../></OverrideMaterial>
 */
export const OverrideMaterial: React.FC<OverrideMaterialProps> = ({
  material,
  active = true,
  shareMaterial = true,
  disposeClones = true,
  renderOrder,
  include,
  exclude,
  children,
}) => {
  const groupRef = useRef<THREE.Group>(null);

  useLayoutEffect(() => {
    const root = groupRef.current;
    if (!root) return;

    const shouldOverride = active && !!material;

    const apply = (mesh: MeshLike) => {
      // store original once
      const ud = mesh.userData as any;
      if (ud[ORIG_KEY] === undefined) {
        ud[ORIG_KEY] = mesh.material;
      }
      // choose material instance
      const mat = shareMaterial ? material! : material!.clone();
      mesh.material = mat;
      if (!shareMaterial) ud[CLONE_KEY] = mat;
      if (renderOrder !== undefined) mesh.renderOrder = renderOrder;
    };

    const restore = (mesh: MeshLike) => {
      const ud = mesh.userData as any;
      if (ud[ORIG_KEY] !== undefined) {
        mesh.material = ud[ORIG_KEY];
        delete ud[ORIG_KEY];
        if (ud[CLONE_KEY]) {
          if (disposeClones && ud[CLONE_KEY].dispose) ud[CLONE_KEY].dispose();
          delete ud[CLONE_KEY];
        }
        if (renderOrder !== undefined) mesh.renderOrder = 0;
      }
    };

    root.traverse((o) => {
      const isMesh = (o as any).isMesh || (o as any).isSkinnedMesh || (o as any).isInstancedMesh;
      if (!isMesh) return;

      if (include && !include(o)) return;
      if (exclude && exclude(o)) return;

      const mesh = o as MeshLike;
      if (shouldOverride) apply(mesh);
      else restore(mesh);
    });

    // Cleanup on unmount or when dependencies change
    return () => {
      root.traverse((o) => {
        const isMesh = (o as any).isMesh || (o as any).isSkinnedMesh || (o as any).isInstancedMesh;
        if (!isMesh) return;
        restore(o as MeshLike);
      });
    };
  }, [active, material, shareMaterial, disposeClones, renderOrder, include, exclude]);

  return <group ref={groupRef}>{children}</group>;
};
